import {
  type ContactFieldKey,
  type ContactInfo,
  contactIncludedKey,
  normalizeContactInfo,
  OPTIONAL_CONTACT_LINK_FIELDS,
  optionalContactLinkEditorKey,
} from '@applicator/shared'
import type { SupabaseClient } from '@supabase/supabase-js'

const CONTACT_FIELDS: ContactFieldKey[] = [
  'firstName',
  'middleName',
  'lastName',
  'email',
  'phone',
  'location',
  'linkedin',
  'substack',
  'github',
]

function contactHasSeedableValues(info: ContactInfo): boolean {
  return CONTACT_FIELDS.some((field) => String(info[field] ?? '').trim().length > 0)
    || (info.additionalLinks?.length ?? 0) > 0
}

/** Most recently updated resume contact_info for the account (optional exclude). */
export async function fetchAccountContactSeed(
  supabase: SupabaseClient,
  options: { excludeResumeId?: string } = {},
): Promise<ContactInfo | null> {
  const { excludeResumeId } = options

  const {
    data: { user },
    error: authError,
  } = await supabase.auth.getUser()
  if (authError) throw authError
  if (!user) return null

  const { data: resumes, error: resumesError } = await supabase
    .from('resumes')
    .select('id, updated_at')
    .eq('user_id', user.id)
    .order('updated_at', { ascending: false })

  if (resumesError) throw resumesError

  const resumeRows = (resumes ?? []).filter((r) => r.id !== excludeResumeId)
  if (resumeRows.length === 0) return null

  const resumeIds = resumeRows.map((r) => r.id)
  const { data: contentRows, error: contentError } = await supabase
    .from('resume_content')
    .select('resume_id, content')
    .in('resume_id', resumeIds)
    .eq('section_type', 'contact_info')

  if (contentError) throw contentError

  const contentByResume = new Map<string, unknown>()
  for (const row of contentRows ?? []) {
    contentByResume.set(row.resume_id, row.content)
  }

  for (const resume of resumeRows) {
    const raw = contentByResume.get(resume.id)
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) continue
    const normalized = normalizeContactInfo(raw as ContactInfo)
    if (contactHasSeedableValues(normalized)) {
      return normalized
    }
  }

  return null
}

/** Profile defaults win when set; otherwise copy values and include flags from account seed. */
export function mergeContactInfoForNewResume(
  profileBase: ContactInfo,
  accountSeed: ContactInfo | null,
): ContactInfo {
  const profile = normalizeContactInfo(profileBase)
  const seed = normalizeContactInfo(accountSeed)
  const out: ContactInfo = { ...seed }

  for (const field of CONTACT_FIELDS) {
    const profileVal = String(profile[field] ?? '').trim()
    const seedVal = String(seed[field] ?? '').trim()
    const includedKey = contactIncludedKey(field)

    if (profileVal) {
      out[field] = profileVal
      if (profile[includedKey] !== undefined) {
        out[includedKey] = profile[includedKey]
      }
    } else if (seedVal) {
      out[field] = seed[field]
      if (seed[includedKey] !== undefined) {
        out[includedKey] = seed[includedKey]
      } else {
        out[includedKey] = true
      }
    } else {
      out[field] = profile[field] ?? ''
      if (profile[includedKey] !== undefined) {
        out[includedKey] = profile[includedKey]
      }
    }
  }

  if (seed.additionalLinks?.length && !profile.additionalLinks?.length) {
    out.additionalLinks = seed.additionalLinks
  } else if (profile.additionalLinks?.length) {
    out.additionalLinks = profile.additionalLinks
  }

  for (const field of OPTIONAL_CONTACT_LINK_FIELDS) {
    const editorKey = optionalContactLinkEditorKey(field)
    if (profile[editorKey] !== undefined) {
      out[editorKey] = profile[editorKey]
    } else if (seed[editorKey] !== undefined) {
      out[editorKey] = seed[editorKey]
    }
  }

  return out
}
